import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import {
  Zap, Search, MessageSquare, Users, LogOut, Menu,
  Trash2, Edit2, DollarSign, Mail, Building2, Send,
  LayoutDashboard, X, Loader2, Plus, Clock, ArrowRight,
  ChevronRight, FileText, Phone, GripVertical, BarChart3, Workflow, Activity, Store,
} from 'lucide-react';
import { HSLogo } from '@/components/HSLogo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = [
  { value: 'contacted', label: 'Contacted', color: 'violet', bg: 'bg-violet-500/10', text: 'text-violet-400', border: 'border-violet-500/30', accent: 'bg-violet-500' },
  { value: 'responded', label: 'Responded', color: 'amber', bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/30', accent: 'bg-amber-500' },
  { value: 'negotiating', label: 'Negotiating', color: 'blue', bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/30', accent: 'bg-blue-500' },
  { value: 'closed', label: 'Closed Won', color: 'emerald', bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/30', accent: 'bg-emerald-500' },
  { value: 'lost', label: 'Lost', color: 'red', bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/30', accent: 'bg-red-500' },
];

const CRM = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [viewMode, setViewMode] = useState('kanban');
  const [selectedLead, setSelectedLead] = useState(null);
  const [activities, setActivities] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [editForm, setEditForm] = useState({});
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => { fetchLeads(); }, []);

  const fetchLeads = async () => {
    try {
      const res = await axios.get(`${API}/leads`);
      setLeads(res.data);
    } catch (err) {
      console.error('Failed to fetch leads:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchActivities = useCallback(async (leadId) => {
    setActivityLoading(true);
    try {
      const res = await axios.get(`${API}/activities/${leadId}`);
      setActivities(res.data);
    } catch (err) {
      console.error('Failed to fetch activities:', err);
    } finally {
      setActivityLoading(false);
    }
  }, []);

  const handleSelectLead = (lead) => {
    setSelectedLead(lead);
    fetchActivities(lead.id);
  };

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      await axios.patch(`${API}/leads/${leadId}`, { status: newStatus });
      toast.success(`Status updated to ${newStatus}`);
      fetchLeads();
      if (selectedLead?.id === leadId) {
        setSelectedLead(prev => ({ ...prev, status: newStatus }));
        fetchActivities(leadId);
      }
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !selectedLead) return;
    try {
      await axios.post(`${API}/activities`, {
        lead_id: selectedLead.id,
        type: 'note',
        content: newNote.trim()
      });
      setNewNote('');
      fetchActivities(selectedLead.id);
      toast.success('Note added');
    } catch (err) {
      toast.error('Failed to add note');
    }
  };

  const handleDeleteLead = async (id) => {
    if (!window.confirm('Delete this lead?')) return;
    try {
      await axios.delete(`${API}/leads/${id}`);
      toast.success('Lead deleted');
      if (selectedLead?.id === id) setSelectedLead(null);
      fetchLeads();
    } catch (err) {
      toast.error('Failed to delete lead');
    }
  };

  const openEditModal = (lead) => {
    setEditForm({
      status: lead.status,
      contact_name: lead.contact_name || '',
      contact_email: lead.contact_email || '',
      notes: lead.notes || '',
      deal_value: lead.deal_value || ''
    });
    setShowEditModal(true);
  };

  const handleUpdateLead = async () => {
    if (!selectedLead) return;
    try {
      const res = await axios.patch(`${API}/leads/${selectedLead.id}`, editForm);
      toast.success('Lead updated!');
      setShowEditModal(false);
      setSelectedLead(res.data);
      fetchLeads();
      fetchActivities(selectedLead.id);
    } catch (err) {
      toast.error('Failed to update lead');
    }
  };

  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: Search, label: 'Job Leads', path: '/jobs' },
    { icon: Users, label: 'Outbound Leads', path: '/crm' },
    { icon: MessageSquare, label: 'Pitch Generator', path: '/pitch' },
    { icon: Store, label: 'Marketplace', path: '/marketplace' },
    { icon: Activity, label: 'API Hub', path: '/hub' },
  ];

  const getStatusConfig = (status) => STATUSES.find(s => s.value === status) || STATUSES[0];

  const pipelineStats = {
    total: leads.length,
    contacted: leads.filter(l => l.status === 'contacted').length,
    responded: leads.filter(l => l.status === 'responded').length,
    negotiating: leads.filter(l => l.status === 'negotiating').length,
    closed: leads.filter(l => l.status === 'closed').length,
    lost: leads.filter(l => l.status === 'lost').length,
    totalValue: leads.filter(l => l.status === 'closed').reduce((s, l) => s + (l.deal_value || 0), 0),
    pipelineValue: leads.filter(l => ['contacted','responded','negotiating'].includes(l.status)).reduce((s, l) => s + (l.deal_value || 0), 0),
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'status_change': return <ArrowRight className="w-3.5 h-3.5 text-blue-400" />;
      case 'pitch_sent': return <Send className="w-3.5 h-3.5 text-emerald-400" />;
      case 'email': return <Mail className="w-3.5 h-3.5 text-amber-400" />;
      case 'call': return <Phone className="w-3.5 h-3.5 text-violet-400" />;
      default: return <FileText className="w-3.5 h-3.5 text-muted-foreground" />;
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex">
      <div className="noise-overlay" />

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#0a0a0a] border-r border-white/5 transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-white/5">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-emerald-500 flex items-center justify-center">
                <Zap className="w-5 h-5 text-black" />
              </div>
              <span className="font-black text-lg tracking-tight">HumanScaled</span>
            </Link>
          </div>
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link key={item.path} to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${isActive ? 'bg-emerald-500/10 text-emerald-400 border-l-2 border-emerald-500' : 'text-muted-foreground hover:text-white hover:bg-white/5'}`}
                >
                  <Icon className="w-5 h-5" />
                  {item.label}
                </Link>
              );
            })}
          </nav>
          <div className="p-4 border-t border-white/5">
            <div className="flex items-center gap-3 px-4 py-3 bg-white/5 rounded-sm mb-3">
              <div className="w-8 h-8 bg-emerald-500/20 flex items-center justify-center text-emerald-400 font-bold text-sm">
                {user?.name?.charAt(0) || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
            </div>
            <Button variant="ghost" onClick={() => { logout(); navigate('/'); }} className="w-full justify-start text-muted-foreground hover:text-white">
              <LogOut className="w-4 h-4 mr-2" /> Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main content */}
      <main className="flex-1 min-h-screen flex flex-col">
        <header className="sticky top-0 z-30 bg-[#050505]/80 backdrop-blur-md border-b border-white/5">
          <div className="flex items-center justify-between h-16 px-6">
            <div className="flex items-center gap-4">
              <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 text-muted-foreground hover:text-white">
                <Menu className="w-6 h-6" />
              </button>
              <h1 className="text-lg font-bold" data-testid="crm-title">CRM Pipeline</h1>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex bg-[#0a0a0a] border border-white/10 rounded-sm overflow-hidden">
                <button
                  onClick={() => setViewMode('kanban')}
                  className={`px-3 py-1.5 text-xs font-mono transition-colors ${viewMode === 'kanban' ? 'bg-emerald-500/20 text-emerald-400' : 'text-muted-foreground hover:text-white'}`}
                  data-testid="view-kanban-btn"
                >
                  BOARD
                </button>
                <button
                  onClick={() => setViewMode('table')}
                  className={`px-3 py-1.5 text-xs font-mono transition-colors ${viewMode === 'table' ? 'bg-emerald-500/20 text-emerald-400' : 'text-muted-foreground hover:text-white'}`}
                  data-testid="view-table-btn"
                >
                  TABLE
                </button>
              </div>
              <Button onClick={() => navigate('/jobs')} className="bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm h-9 text-sm" data-testid="add-lead-cta">
                <Plus className="w-4 h-4 mr-1" /> Add Lead
              </Button>
            </div>
          </div>
        </header>

        <div className="p-6 lg:p-8 space-y-6 flex-1">
          {/* Pipeline Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
            {[
              { label: 'Total', value: pipelineStats.total, cls: 'text-white' },
              { label: 'Contacted', value: pipelineStats.contacted, cls: 'text-violet-400' },
              { label: 'Responded', value: pipelineStats.responded, cls: 'text-amber-400' },
              { label: 'Negotiating', value: pipelineStats.negotiating, cls: 'text-blue-400' },
              { label: 'Closed', value: pipelineStats.closed, cls: 'text-emerald-400' },
              { label: 'Revenue', value: `$${pipelineStats.totalValue.toLocaleString()}`, cls: 'text-emerald-400' },
              { label: 'Pipeline', value: `$${pipelineStats.pipelineValue.toLocaleString()}`, cls: 'text-blue-400' },
            ].map((stat, i) => (
              <div key={i} className="bg-[#0a0a0a] border border-white/5 p-3" data-testid={`stat-${stat.label.toLowerCase()}`}>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">{stat.label}</p>
                <p className={`font-mono text-lg font-bold ${stat.cls}`}>{stat.value}</p>
              </div>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
            </div>
          ) : leads.length === 0 ? (
            <div className="bg-[#0a0a0a] border border-white/5 p-12 text-center">
              <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-bold mb-2">No leads yet</h3>
              <p className="text-muted-foreground mb-4">Find job opportunities and add them as leads to track your pipeline.</p>
              <Button onClick={() => navigate('/jobs')} className="bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm" data-testid="empty-find-btn">
                Find Opportunities
              </Button>
            </div>
          ) : viewMode === 'kanban' ? (
            /* Kanban Board */
            <div className="flex gap-4 overflow-x-auto pb-4 -mx-2 px-2" data-testid="kanban-board">
              {STATUSES.filter(s => s.value !== 'lost').map(status => {
                const columnLeads = leads.filter(l => l.status === status.value);
                return (
                  <div key={status.value} className="flex-shrink-0 w-72" data-testid={`kanban-column-${status.value}`}>
                    {/* Column Header */}
                    <div className={`flex items-center justify-between px-3 py-2 mb-3 border-b-2 ${status.border}`}>
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${status.accent}`} />
                        <span className={`text-sm font-bold ${status.text}`}>{status.label}</span>
                      </div>
                      <span className={`text-xs font-mono px-1.5 py-0.5 ${status.bg} ${status.text}`}>{columnLeads.length}</span>
                    </div>
                    {/* Cards */}
                    <div className="space-y-2 min-h-[200px]">
                      {columnLeads.map(lead => (
                        <div
                          key={lead.id}
                          onClick={() => handleSelectLead(lead)}
                          className={`bg-[#0a0a0a] border p-3 cursor-pointer transition-all hover:border-white/20 group ${
                            selectedLead?.id === lead.id ? `border-white/30 ring-1 ring-white/10` : 'border-white/5'
                          }`}
                          data-testid={`kanban-card-${lead.id}`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <Building2 className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                              <span className="font-medium text-sm truncate">{lead.company}</span>
                            </div>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDeleteLead(lead.id); }}
                              className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:text-red-400"
                              data-testid={`delete-lead-${lead.id}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                          {lead.contact_name && (
                            <p className="text-xs text-muted-foreground mb-1 truncate">{lead.contact_name}</p>
                          )}
                          {lead.deal_value > 0 && (
                            <div className="flex items-center gap-1 mt-2">
                              <DollarSign className="w-3 h-3 text-emerald-400" />
                              <span className="text-xs font-mono text-emerald-400">${lead.deal_value.toLocaleString()}/mo</span>
                            </div>
                          )}
                          <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/5">
                            <span className="text-[10px] text-muted-foreground">{new Date(lead.created_at).toLocaleDateString()}</span>
                            {status.value !== 'closed' && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const idx = STATUSES.findIndex(s => s.value === status.value);
                                  if (idx < STATUSES.length - 2) handleStatusChange(lead.id, STATUSES[idx + 1].value);
                                }}
                                className="text-[10px] font-mono text-emerald-400 hover:text-emerald-300 flex items-center gap-0.5 transition-colors"
                                data-testid={`advance-lead-${lead.id}`}
                              >
                                ADVANCE <ChevronRight className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              {/* Lost column (collapsed) */}
              {leads.filter(l => l.status === 'lost').length > 0 && (
                <div className="flex-shrink-0 w-48">
                  <div className="flex items-center justify-between px-3 py-2 mb-3 border-b-2 border-red-500/30">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-red-500" />
                      <span className="text-sm font-bold text-red-400">Lost</span>
                    </div>
                    <span className="text-xs font-mono px-1.5 py-0.5 bg-red-500/10 text-red-400">
                      {leads.filter(l => l.status === 'lost').length}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {leads.filter(l => l.status === 'lost').map(lead => (
                      <div key={lead.id} onClick={() => handleSelectLead(lead)}
                        className="bg-[#0a0a0a] border border-white/5 p-2 cursor-pointer opacity-60 hover:opacity-100 transition-all"
                      >
                        <span className="text-xs text-muted-foreground truncate block">{lead.company}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Table View */
            <div className="bg-[#0a0a0a] border border-white/5 overflow-hidden" data-testid="table-view">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/5">
                      <th className="text-left p-4 text-xs text-muted-foreground uppercase font-medium">Company</th>
                      <th className="text-left p-4 text-xs text-muted-foreground uppercase font-medium">Contact</th>
                      <th className="text-left p-4 text-xs text-muted-foreground uppercase font-medium">Status</th>
                      <th className="text-left p-4 text-xs text-muted-foreground uppercase font-medium">Deal Value</th>
                      <th className="text-left p-4 text-xs text-muted-foreground uppercase font-medium">Date</th>
                      <th className="text-right p-4 text-xs text-muted-foreground uppercase font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {leads.map((lead, i) => {
                      const sc = getStatusConfig(lead.status);
                      return (
                        <tr key={lead.id} className="hover:bg-white/5 transition-colors cursor-pointer" onClick={() => handleSelectLead(lead)} data-testid={`crm-lead-row-${i}`}>
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-white/5 flex items-center justify-center">
                                <Building2 className="w-4 h-4 text-muted-foreground" />
                              </div>
                              <span className="font-medium">{lead.company}</span>
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="text-sm">
                              <p>{lead.contact_name || '-'}</p>
                              <p className="text-muted-foreground text-xs">{lead.contact_email || '-'}</p>
                            </div>
                          </td>
                          <td className="p-4">
                            <span className={`px-2 py-1 text-xs font-mono uppercase ${sc.bg} ${sc.text}`}>{lead.status}</span>
                          </td>
                          <td className="p-4 font-mono">{lead.deal_value ? `$${lead.deal_value.toLocaleString()}` : '-'}</td>
                          <td className="p-4 text-sm text-muted-foreground">{new Date(lead.created_at).toLocaleDateString()}</td>
                          <td className="p-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openEditModal(lead); handleSelectLead(lead); }}
                                className="h-8 w-8 p-0 text-muted-foreground hover:text-white" data-testid={`edit-lead-btn-${i}`}>
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDeleteLead(lead.id); }}
                                className="h-8 w-8 p-0 text-muted-foreground hover:text-red-400" data-testid={`delete-lead-btn-${i}`}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Lead Detail Slide-out Panel */}
      {selectedLead && (
        <aside className="fixed lg:static right-0 top-0 bottom-0 z-50 w-full lg:w-96 bg-[#0a0a0a] border-l border-white/5 flex flex-col overflow-hidden" data-testid="lead-detail-panel">
          {/* Panel Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/5">
            <h2 className="font-bold text-base truncate">{selectedLead.company}</h2>
            <div className="flex items-center gap-1">
              <button onClick={() => { openEditModal(selectedLead); }} className="p-1.5 text-muted-foreground hover:text-white transition-colors" data-testid="panel-edit-btn">
                <Edit2 className="w-4 h-4" />
              </button>
              <button onClick={() => setSelectedLead(null)} className="p-1.5 text-muted-foreground hover:text-white transition-colors" data-testid="panel-close-btn">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Lead Info */}
          <div className="p-4 border-b border-white/5 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground uppercase">Status</span>
              <Select value={selectedLead.status} onValueChange={(v) => handleStatusChange(selectedLead.id, v)}>
                <SelectTrigger className="w-36 h-7 text-xs bg-black/30 border-white/10 rounded-sm" data-testid="panel-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0a0a0a] border-white/10">
                  {STATUSES.map(s => (
                    <SelectItem key={s.value} value={s.value}>
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${s.accent}`} />
                        {s.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedLead.contact_name && (
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground uppercase">Contact</span>
                <span className="text-sm">{selectedLead.contact_name}</span>
              </div>
            )}
            {selectedLead.contact_email && (
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground uppercase">Email</span>
                <a href={`mailto:${selectedLead.contact_email}`} className="text-sm text-emerald-400 hover:underline">{selectedLead.contact_email}</a>
              </div>
            )}
            {selectedLead.deal_value > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground uppercase">Deal Value</span>
                <span className="text-sm font-mono text-emerald-400">${selectedLead.deal_value.toLocaleString()}/mo</span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground uppercase">Created</span>
              <span className="text-sm text-muted-foreground">{new Date(selectedLead.created_at).toLocaleDateString()}</span>
            </div>
            {selectedLead.notes && (
              <div className="bg-black/30 border border-white/5 p-3 rounded-sm">
                <span className="text-[10px] text-muted-foreground uppercase block mb-1">Notes</span>
                <p className="text-sm text-muted-foreground">{selectedLead.notes}</p>
              </div>
            )}
          </div>

          {/* Activity Timeline */}
          <div className="flex-1 overflow-y-auto p-4">
            <h3 className="text-xs text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
              <Clock className="w-3.5 h-3.5" /> Activity
            </h3>
            {activityLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            ) : activities.length > 0 ? (
              <div className="space-y-3">
                {activities.map((act) => (
                  <div key={act.id} className="flex gap-3 group" data-testid={`activity-${act.id}`}>
                    <div className="flex flex-col items-center">
                      <div className="w-6 h-6 bg-white/5 flex items-center justify-center flex-shrink-0">
                        {getActivityIcon(act.type)}
                      </div>
                      <div className="w-px flex-1 bg-white/5 mt-1" />
                    </div>
                    <div className="pb-3 min-w-0">
                      <p className="text-sm">{act.content}</p>
                      <p className="text-[10px] text-muted-foreground mt-1">{new Date(act.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">No activity yet</p>
            )}
          </div>

          {/* Add Note */}
          <div className="p-4 border-t border-white/5">
            <div className="flex gap-2">
              <Input
                placeholder="Add a note..."
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddNote()}
                className="flex-1 bg-black/30 border-white/10 rounded-sm h-9 text-sm"
                data-testid="add-note-input"
              />
              <Button onClick={handleAddNote} disabled={!newNote.trim()} className="bg-emerald-500 text-black hover:bg-emerald-400 rounded-sm h-9 px-3" data-testid="add-note-btn">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </aside>
      )}

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Lead: {selectedLead?.company}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-xs text-muted-foreground uppercase mb-2 block">Status</label>
              <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                <SelectTrigger className="bg-black/50 border-white/10 rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0a0a0a] border-white/10">
                  {STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase mb-2 block">Contact Name</label>
              <Input value={editForm.contact_name} onChange={(e) => setEditForm({ ...editForm, contact_name: e.target.value })} className="bg-black/50 border-white/10 rounded-sm" placeholder="John Smith" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase mb-2 block">Contact Email</label>
              <Input type="email" value={editForm.contact_email} onChange={(e) => setEditForm({ ...editForm, contact_email: e.target.value })} className="bg-black/50 border-white/10 rounded-sm" placeholder="john@company.com" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase mb-2 block">Deal Value ($/mo)</label>
              <Input type="number" value={editForm.deal_value} onChange={(e) => setEditForm({ ...editForm, deal_value: parseFloat(e.target.value) || '' })} className="bg-black/50 border-white/10 rounded-sm" placeholder="1500" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase mb-2 block">Notes</label>
              <Textarea value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} className="bg-black/50 border-white/10 rounded-sm min-h-[80px]" placeholder="Add notes..." />
            </div>
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowEditModal(false)} className="flex-1 border-white/10 rounded-sm">Cancel</Button>
              <Button onClick={handleUpdateLead} className="flex-1 bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm" data-testid="save-lead-btn">Save Changes</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CRM;
